import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Bot, ArrowLeft, CheckCircle2, Sparkles, Brain, Image, FileText, Video, Code, MessageSquare, TrendingUp, Zap, Users, Edit3, Plus, Music, Settings, X, Cpu, Hand, Accessibility, Type } from 'lucide-react';
import { AgentList } from '../components/agents/AgentList';
import { AgentCreation } from '../components/agents/AgentCreation';
import { CustomModelWizard } from '../components/agents/CustomModelWizard';
import { useAgentStore } from '../store/agent.store';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
// Agent Details Page
function AgentDetailsPage() {
    const { agentId } = useParams();
    const { getAgentById } = useAgentStore();
    const agent = agentId ? getAgentById(agentId) : null;
    if (!agent) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsxs("div", { className: "text-center", children: [_jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Agent Not Found" }), _jsx("p", { className: "text-gray-600", children: "The agent you're looking for doesn't exist." })] }) }));
    }
    return (_jsxs("div", { className: "p-6", children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900 mb-4", children: agent.name }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-lg p-6", children: [_jsx("p", { className: "text-gray-600 mb-4", children: agent.description }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [_jsxs("div", { children: [_jsx("h3", { className: "font-semibold text-gray-900 mb-2", children: "Basic Information" }), _jsxs("dl", { className: "space-y-2 text-sm", children: [_jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Type:" }), _jsx("dd", { className: "text-gray-900", children: agent.type })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Status:" }), _jsx("dd", { className: "text-gray-900", children: agent.status })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Version:" }), _jsx("dd", { className: "text-gray-900", children: agent.version })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Created:" }), _jsx("dd", { className: "text-gray-900", children: new Date(agent.createdAt).toLocaleDateString() })] })] })] }), _jsxs("div", { children: [_jsx("h3", { className: "font-semibold text-gray-900 mb-2", children: "Performance Metrics" }), _jsxs("dl", { className: "space-y-2 text-sm", children: [_jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Tasks Completed:" }), _jsx("dd", { className: "text-gray-900", children: agent.metrics.tasksCompleted })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Success Rate:" }), _jsxs("dd", { className: "text-gray-900", children: [Math.round(agent.metrics.successRate * 100), "%"] })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Cost Savings:" }), _jsxs("dd", { className: "text-gray-900", children: ["$", agent.metrics.costSavings.toLocaleString()] })] }), _jsxs("div", { children: [_jsx("dt", { className: "font-medium text-gray-700", children: "Current Tasks:" }), _jsx("dd", { className: "text-gray-900", children: agent.metrics.currentTasks })] })] })] })] }), agent.capabilities.length > 0 && (_jsxs("div", { className: "mt-6", children: [_jsx("h3", { className: "font-semibold text-gray-900 mb-2", children: "Capabilities" }), _jsx("div", { className: "flex flex-wrap gap-2", children: agent.capabilities.map((capability) => (_jsx("span", { className: "px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm", children: capability }, capability))) })] })), agent.tags.length > 0 && (_jsxs("div", { className: "mt-6", children: [_jsx("h3", { className: "font-semibold text-gray-900 mb-2", children: "Tags" }), _jsx("div", { className: "flex flex-wrap gap-2", children: agent.tags.map((tag) => (_jsx("span", { className: "px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm", children: tag }, tag))) })] }))] })] }));
}
// Agent Creation Page
function AgentCreationPage() {
    const navigate = useNavigate();
    const handleAgentCreated = (agentId) => {
        navigate(`/app/agents/${agentId}`);
    };
    const handleClose = () => {
        navigate('/app/agents');
    };
    return (_jsx(AgentCreation, { onClose: handleClose, onAgentCreated: handleAgentCreated }));
}
// Main Agents Page with Onboarding
function AgentsMainPage() {
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const { getAgents, fetchAgents, isLoading, error, clearError } = useAgentStore();
    const [onboardingStep, setOnboardingStep] = useState('welcome');
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Initializing AI Agent workspace...');
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [showCustomModelModal, setShowCustomModelModal] = useState(false);
    const [customModelType, setCustomModelType] = useState(null);
    const [customModelStep, setCustomModelStep] = useState(1);
    const [customModelFormData, setCustomModelFormData] = useState({});
    const agents = getAgents();
    // Check if user has completed onboarding before
    useEffect(() => {
        const hasCompletedOnboarding = localStorage.getItem('agents_onboarding_completed');
        if (hasCompletedOnboarding === 'true') {
            setOnboardingStep('complete');
        }
    }, []);
    // Fetch agents on component mount
    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);
    // Simulate loading progress
    useEffect(() => {
        if (onboardingStep === 'loading') {
            const loadingSteps = [
                { progress: 25, message: 'Setting up AI workspace...', delay: 800 },
                { progress: 50, message: 'Configuring model preferences...', delay: 1000 },
                { progress: 75, message: 'Preparing agent templates...', delay: 900 },
                { progress: 100, message: 'Almost ready...', delay: 700 }
            ];
            let currentStep = 0;
            const runNextStep = () => {
                if (currentStep < loadingSteps.length) {
                    const step = loadingSteps[currentStep];
                    setTimeout(() => {
                        setLoadingProgress(step.progress);
                        setLoadingMessage(step.message);
                        currentStep++;
                        runNextStep();
                    }, step.delay);
                }
                else {
                    setTimeout(() => {
                        localStorage.setItem('agents_onboarding_completed', 'true');
                        if (selectedModel) {
                            localStorage.setItem('agents_preferred_model', selectedModel);
                        }
                        setOnboardingStep('complete');
                    }, 500);
                }
            };
            runNextStep();
        }
    }, [onboardingStep, selectedModel]);
    const handleSkipOnboarding = () => {
        localStorage.setItem('agents_onboarding_completed', 'true');
        setOnboardingStep('complete');
    };
    const handleActionChoice = (action) => {
        setSelectedAction(action);
        if (action === 'select-existing' && agents.length > 0) {
            // Skip model selection if selecting existing
            setOnboardingStep('loading');
        }
        else {
            setOnboardingStep('model-selection');
        }
    };
    const handleModelSelection = (modelId) => {
        setSelectedModel(modelId);
    };
    const handleContinueWithModel = () => {
        if (selectedModel) {
            setOnboardingStep('loading');
        }
    };
    const handleAgentSelect = (agent) => {
        navigate(`/app/agents/${agent.id}`);
    };
    const handleCreateAgent = () => {
        navigate('/app/agents/create');
    };
    const handleShowOnboarding = () => {
        localStorage.removeItem('agents_onboarding_completed');
        setOnboardingStep('welcome');
    };
    // AI Models with use case recommendations
    const aiModels = [
        {
            id: 'gpt-4',
            name: 'GPT-4',
            provider: 'OpenAI',
            icon: Brain,
            color: '#10B981',
            description: 'Most capable model for complex reasoning',
            bestFor: ['Legal writing', 'Research', 'Complex analysis', 'Strategic planning'],
            performance: 'High',
            cost: '$$$$'
        },
        {
            id: 'gpt-4-vision',
            name: 'GPT-4 Vision',
            provider: 'OpenAI',
            icon: Image,
            color: '#8B5CF6',
            description: 'Multimodal model with vision capabilities',
            bestFor: ['Image analysis', 'Visual content', 'Document OCR', 'Design feedback'],
            performance: 'High',
            cost: '$$$$'
        },
        {
            id: 'claude-3-opus',
            name: 'Claude 3 Opus',
            provider: 'Anthropic',
            icon: FileText,
            color: '#F59E0B',
            description: 'Best for creative and technical writing',
            bestFor: ['Content creation', 'Documentation', 'Code review', 'Storytelling'],
            performance: 'High',
            cost: '$$$$'
        },
        {
            id: 'claude-3-sonnet',
            name: 'Claude 3 Sonnet',
            provider: 'Anthropic',
            icon: MessageSquare,
            color: '#3B82F6',
            description: 'Balanced performance and cost',
            bestFor: ['Customer service', 'Chat support', 'General tasks', 'Email responses'],
            performance: 'Medium-High',
            cost: '$$$'
        },
        {
            id: 'dall-e-3',
            name: 'DALL-E 3',
            provider: 'OpenAI',
            icon: Image,
            color: '#EC4899',
            description: 'Advanced image generation',
            bestFor: ['Image generation', 'Creative design', 'Marketing visuals', 'Illustrations'],
            performance: 'High',
            cost: '$$'
        },
        {
            id: 'stable-diffusion-xl',
            name: 'Stable Diffusion XL',
            provider: 'Stability AI',
            icon: Sparkles,
            color: '#A855F7',
            description: 'Open-source image generation',
            bestFor: ['Image generation', 'Art creation', 'Style transfer', 'Batch processing'],
            performance: 'Medium',
            cost: '$'
        },
        {
            id: 'whisper',
            name: 'Whisper',
            provider: 'OpenAI',
            icon: Video,
            color: '#06B6D4',
            description: 'Speech recognition and transcription',
            bestFor: ['Video transcription', 'Audio processing', 'Meeting notes', 'Subtitles'],
            performance: 'High',
            cost: '$'
        },
        {
            id: 'codex',
            name: 'Codex',
            provider: 'OpenAI',
            icon: Code,
            color: '#EF4444',
            description: 'Code generation and analysis',
            bestFor: ['Code generation', 'Bug fixing', 'Code review', 'API documentation'],
            performance: 'High',
            cost: '$$$'
        },
        {
            id: 'gpt-3.5-turbo',
            name: 'GPT-3.5 Turbo',
            provider: 'OpenAI',
            icon: Zap,
            color: '#84CC16',
            description: 'Fast and cost-effective',
            bestFor: ['Quick tasks', 'Simple automation', 'Data processing', 'Classification'],
            performance: 'Medium',
            cost: '$'
        },
        {
            id: 'gemini-pro',
            name: 'Gemini Pro',
            provider: 'Google',
            icon: Brain,
            color: '#4285F4',
            description: 'Google\'s most capable multimodal model',
            bestFor: ['Complex reasoning', 'Multimodal tasks', 'Long context', 'Multilingual content'],
            performance: 'High',
            cost: '$$$'
        },
        {
            id: 'gemini-pro-vision',
            name: 'Gemini Pro Vision',
            provider: 'Google',
            icon: Image,
            color: '#EA4335',
            description: 'Advanced vision and multimodal understanding',
            bestFor: ['Image understanding', 'Visual Q&A', 'Chart analysis', 'Diagram interpretation'],
            performance: 'High',
            cost: '$$$'
        },
        {
            id: 'gemini-ultra',
            name: 'Gemini Ultra',
            provider: 'Google',
            icon: Sparkles,
            color: '#FBBC04',
            description: 'Most capable Gemini model for complex tasks',
            bestFor: ['Advanced reasoning', 'Research', 'Scientific analysis', 'Expert-level tasks'],
            performance: 'Very High',
            cost: '$$$$'
        },
        {
            id: 'gemini-nano',
            name: 'Gemini Nano',
            provider: 'Google',
            icon: Zap,
            color: '#34A853',
            description: 'Efficient on-device AI model',
            bestFor: ['Mobile apps', 'Edge computing', 'Privacy-focused tasks', 'Offline processing'],
            performance: 'Medium',
            cost: '$'
        },
        {
            id: 'elevenlabs-multilingual-v2',
            name: 'ElevenLabs Multilingual v2',
            provider: 'ElevenLabs',
            icon: MessageSquare,
            color: '#7C3AED',
            description: 'High-quality AI voice synthesis in 29 languages',
            bestFor: ['Voice generation', 'Audiobooks', 'Podcasts', 'Multilingual content'],
            performance: 'High',
            cost: '$$'
        },
        {
            id: 'elevenlabs-turbo-v2',
            name: 'ElevenLabs Turbo v2',
            provider: 'ElevenLabs',
            icon: Zap,
            color: '#8B5CF6',
            description: 'Fast, low-latency voice synthesis',
            bestFor: ['Real-time voice', 'Conversational AI', 'Voice assistants', 'Live interactions'],
            performance: 'High',
            cost: '$$'
        },
        {
            id: 'elevenlabs-voice-cloning',
            name: 'ElevenLabs Voice Cloning',
            provider: 'ElevenLabs',
            icon: Users,
            color: '#A855F7',
            description: 'Create custom AI voices from samples',
            bestFor: ['Custom voices', 'Brand voice', 'Character voices', 'Personalization'],
            performance: 'High',
            cost: '$$$'
        },
        {
            id: 'hume-empathic-voice',
            name: 'Hume Empathic Voice',
            provider: 'Hume AI',
            icon: MessageSquare,
            color: '#FF6B9D',
            description: 'AI voice with emotional intelligence and empathy',
            bestFor: ['Empathic conversation', 'Mental health support', 'Customer care', 'Emotional AI'],
            performance: 'High',
            cost: '$$$'
        },
        {
            id: 'hume-expression-measurement',
            name: 'Hume Expression Measurement',
            provider: 'Hume AI',
            icon: TrendingUp,
            color: '#FF8FB1',
            description: 'Analyze emotions from voice, text, and facial expressions',
            bestFor: ['Emotion detection', 'Sentiment analysis', 'User research', 'Feedback analysis'],
            performance: 'High',
            cost: '$$'
        },
        {
            id: 'suno-v3',
            name: 'Suno v3',
            provider: 'Suno AI',
            icon: Music,
            color: '#FF6B35',
            description: 'AI music and song generation from text',
            bestFor: ['Music creation', 'Jingles', 'Background music', 'Original songs'],
            performance: 'High',
            cost: '$$'
        },
        {
            id: 'suno-bark',
            name: 'Suno Bark',
            provider: 'Suno AI',
            icon: Music,
            color: '#F7931E',
            description: 'Text-to-audio model for speech and sound effects',
            bestFor: ['Sound effects', 'Non-speech audio', 'Audio assets', 'Creative audio'],
            performance: 'Medium-High',
            cost: '$'
        },
        {
            id: 'llama-3.2',
            name: 'Llama 3.2',
            provider: 'Meta',
            icon: Brain,
            color: '#0668E1',
            description: 'Free, open-source model with strong reasoning',
            bestFor: ['General tasks', 'Content creation', 'Summarization', 'Q&A'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'llama-3.1-405b',
            name: 'Llama 3.1 405B',
            provider: 'Meta',
            icon: Brain,
            color: '#0081FB',
            description: 'Meta\'s largest open-source model',
            bestFor: ['Complex reasoning', 'Research', 'Advanced tasks', 'Long context'],
            performance: 'Very High',
            cost: 'Free'
        },
        {
            id: 'mistral-7b',
            name: 'Mistral 7B',
            provider: 'Mistral AI',
            icon: Zap,
            color: '#FF7000',
            description: 'Fast, efficient open-source model',
            bestFor: ['Quick tasks', 'Coding', 'Analysis', 'Cost-effective automation'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'mixtral-8x7b',
            name: 'Mixtral 8x7B',
            provider: 'Mistral AI',
            icon: Brain,
            color: '#F2A900',
            description: 'Mixture-of-experts model, excellent performance',
            bestFor: ['Complex tasks', 'Multilingual', 'Code generation', 'Reasoning'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'phi-3-mini',
            name: 'Phi-3 Mini',
            provider: 'Microsoft',
            icon: Zap,
            color: '#00A4EF',
            description: 'Compact model optimized for efficiency',
            bestFor: ['Mobile apps', 'Edge devices', 'Quick responses', 'Resource-constrained'],
            performance: 'Medium',
            cost: 'Free'
        },
        {
            id: 'phi-3-medium',
            name: 'Phi-3 Medium',
            provider: 'Microsoft',
            icon: Brain,
            color: '#0078D4',
            description: 'Balanced performance and efficiency',
            bestFor: ['General purpose', 'Business apps', 'Customer service', 'Data analysis'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'gemma-7b',
            name: 'Gemma 7B',
            provider: 'Google',
            icon: Brain,
            color: '#4285F4',
            description: 'Google\'s open-source lightweight model',
            bestFor: ['General tasks', 'Chatbots', 'Text generation', 'Education'],
            performance: 'Medium',
            cost: 'Free'
        },
        {
            id: 'gemma-2-27b',
            name: 'Gemma 2 27B',
            provider: 'Google',
            icon: Brain,
            color: '#34A853',
            description: 'Larger Gemma with enhanced capabilities',
            bestFor: ['Complex reasoning', 'Long conversations', 'Research', 'Analysis'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'vicuna-13b',
            name: 'Vicuna 13B',
            provider: 'LMSYS',
            icon: MessageSquare,
            color: '#9333EA',
            description: 'Open-source chatbot trained from Llama',
            bestFor: ['Conversations', 'Customer support', 'Q&A', 'General assistance'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'llava-1.6',
            name: 'LLaVA 1.6',
            provider: 'Open Source',
            icon: Image,
            color: '#EC4899',
            description: 'Free visual language model',
            bestFor: ['Image understanding', 'Visual Q&A', 'Accessibility', 'Education'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'falcon-180b',
            name: 'Falcon 180B',
            provider: 'TII',
            icon: Brain,
            color: '#DC2626',
            description: 'Powerful open-source model from UAE',
            bestFor: ['Research', 'Complex tasks', 'Multilingual', 'Enterprise use'],
            performance: 'Very High',
            cost: 'Free'
        },
        {
            id: 'deepseek-coder',
            name: 'DeepSeek Coder',
            provider: 'DeepSeek',
            icon: Code,
            color: '#10B981',
            description: 'Free, specialized coding model',
            bestFor: ['Code generation', 'Debugging', 'Code review', 'Programming help'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'braille-translator',
            name: 'Braille Translator',
            provider: 'Accessibility AI',
            icon: Hand,
            color: '#FF6B6B',
            description: 'Convert text to/from Braille for deafblind users',
            bestFor: ['Braille translation', 'Accessible content', 'Deafblind support', 'Text-to-Braille'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'tactile-haptic-ai',
            name: 'Tactile Haptic AI',
            provider: 'Accessibility AI',
            icon: Hand,
            color: '#FF8E53',
            description: 'Generate haptic feedback patterns for tactile communication',
            bestFor: ['Haptic feedback', 'Tactile alerts', 'Deafblind communication', 'Touch-based UI'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'signllm',
            name: 'SignLLM',
            provider: 'Accessibility AI',
            icon: Hand,
            color: '#4ECDC4',
            description: 'Sign language translation and generation',
            bestFor: ['Sign language', 'ASL/BSL translation', 'Deaf support', 'Visual communication'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'easy-read-ai',
            name: 'Easy Read AI',
            provider: 'Accessibility AI',
            icon: Type,
            color: '#95E1D3',
            description: 'Simplify text for cognitive accessibility',
            bestFor: ['Text simplification', 'Easy reading', 'Cognitive support', 'Clear language'],
            performance: 'Medium-High',
            cost: 'Free'
        },
        {
            id: 'accessible-alt-text',
            name: 'Accessible Alt-Text Generator',
            provider: 'Accessibility AI',
            icon: Accessibility,
            color: '#F38181',
            description: 'Generate detailed descriptions for screen readers',
            bestFor: ['Alt-text', 'Image description', 'Screen readers', 'Visual impairment'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'protactile-ai',
            name: 'ProTactile AI',
            provider: 'Accessibility AI',
            icon: Hand,
            color: '#AA96DA',
            description: 'ProTactile Sign Language (PTSL) support for deafblind',
            bestFor: ['Deafblind communication', 'PTSL translation', 'Tactile signing', 'Touch-based language'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'clear-speech-ai',
            name: 'Clear Speech AI',
            provider: 'Accessibility AI',
            icon: MessageSquare,
            color: '#FCBAD3',
            description: 'Enhanced speech output for hearing aids and cochlear implants',
            bestFor: ['Clear speech', 'Hearing support', 'Audio enhancement', 'Speech clarity'],
            performance: 'High',
            cost: 'Free'
        },
        {
            id: 'multimodal-access',
            name: 'Multimodal Access AI',
            provider: 'Accessibility AI',
            icon: Accessibility,
            color: '#A8D8EA',
            description: 'Convert between text, braille, audio, and tactile formats',
            bestFor: ['Multimodal conversion', 'Format translation', 'Universal access', 'Cross-sensory'],
            performance: 'Very High',
            cost: 'Free'
        }
    ];
    // Welcome Screen
    if (onboardingStep === 'welcome') {
        return (_jsx("div", { className: "flex flex-col h-full items-center justify-center", style: { backgroundColor: 'var(--bg-base)' }, children: _jsxs("div", { className: "max-w-4xl w-full px-8", children: [_jsx("div", { className: "flex justify-end mb-8", children: _jsx("button", { onClick: handleSkipOnboarding, className: "text-sm px-4 py-2 rounded-lg hover:bg-white/05 transition-colors", style: { color: 'var(--text-secondary)' }, children: "Skip for now" }) }), _jsxs("div", { className: "text-center mb-12", children: [_jsx("div", { className: "flex items-center justify-center gap-3 mb-4", children: _jsx(Bot, { className: "w-16 h-16", style: { color: '#6366F1' } }) }), _jsxs("h1", { className: "text-5xl font-light mb-2", style: { color: 'var(--text-primary)' }, children: ["Welcome to AI Agents", userProfile?.full_name ? `, ${userProfile.full_name.split(' ')[0]}` : '', "!"] }), _jsx("p", { className: "text-2xl font-light", style: { color: '#6366F1' }, children: "Your autonomous AI workforce awaits" })] }), _jsx("div", { className: "p-8 rounded-3xl border mb-8", style: {
                            backgroundColor: 'var(--bg-elevated)',
                            borderColor: 'var(--border-color)'
                        }, children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-6", children: [_jsxs("div", { className: "text-center", children: [_jsx("div", { className: "flex justify-center mb-3", children: _jsx("div", { className: "w-16 h-16 rounded-2xl flex items-center justify-center", style: { backgroundColor: '#6366F120' }, children: _jsx(Bot, { className: "w-8 h-8", style: { color: '#6366F1' } }) }) }), _jsx("h3", { className: "font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Create AI Agents" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Build autonomous agents for any task - from customer service to content creation" })] }), _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "flex justify-center mb-3", children: _jsx("div", { className: "w-16 h-16 rounded-2xl flex items-center justify-center", style: { backgroundColor: '#10B98120' }, children: _jsx(Brain, { className: "w-8 h-8", style: { color: '#10B981' } }) }) }), _jsx("h3", { className: "font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Choose AI Models" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Select from top LLMs and SLMs optimized for specific use cases" })] }), _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "flex justify-center mb-3", children: _jsx("div", { className: "w-16 h-16 rounded-2xl flex items-center justify-center", style: { backgroundColor: '#F59E0B20' }, children: _jsx(TrendingUp, { className: "w-8 h-8", style: { color: '#F59E0B' } }) }) }), _jsx("h3", { className: "font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Track Performance" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Monitor agent performance, cost savings, and success rates in real-time" })] })] }) }), _jsx("div", { className: "flex items-center justify-center", children: _jsxs(Button, { onClick: () => setOnboardingStep('action-choice'), style: { backgroundColor: '#6366F1', color: 'white' }, className: "px-8 py-3 rounded-xl flex items-center gap-2 text-base", children: ["Get Started", _jsx(ArrowLeft, { className: "w-5 h-5 rotate-180" })] }) })] }) }));
    }
    // Action Choice Screen
    if (onboardingStep === 'action-choice') {
        return (_jsx("div", { className: "flex flex-col h-full items-center justify-center", style: { backgroundColor: 'var(--bg-base)' }, children: _jsxs("div", { className: "max-w-4xl w-full px-8", children: [_jsx("div", { className: "flex justify-end mb-8", children: _jsx("button", { onClick: handleSkipOnboarding, className: "text-sm px-4 py-2 rounded-lg hover:bg-white/05 transition-colors", style: { color: 'var(--text-secondary)' }, children: "Skip for now" }) }), _jsxs("div", { className: "text-center mb-12", children: [_jsx("h2", { className: "text-4xl font-light mb-3", style: { color: 'var(--text-primary)' }, children: "What would you like to do?" }), _jsx("p", { className: "text-lg", style: { color: 'var(--text-secondary)' }, children: "Create a new AI agent or manage your existing ones" })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6 mb-8", children: [_jsx("button", { onClick: () => handleActionChoice('create-new'), className: "p-8 rounded-3xl border-2 transition-all hover:scale-105", style: {
                                    backgroundColor: selectedAction === 'create-new' ? '#6366F120' : 'var(--bg-elevated)',
                                    borderColor: selectedAction === 'create-new' ? '#6366F1' : 'var(--border-color)'
                                }, children: _jsxs("div", { className: "flex flex-col items-center text-center", children: [_jsx("div", { className: "w-20 h-20 rounded-3xl flex items-center justify-center mb-4", style: { backgroundColor: '#6366F120' }, children: _jsx(Plus, { className: "w-10 h-10", style: { color: '#6366F1' } }) }), _jsx("h3", { className: "text-2xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Create New Agent" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Build a custom AI agent from scratch with your preferred model and capabilities" })] }) }), _jsx("button", { onClick: () => handleActionChoice('select-existing'), className: "p-8 rounded-3xl border-2 transition-all hover:scale-105", style: {
                                    backgroundColor: selectedAction === 'select-existing' ? '#6366F120' : 'var(--bg-elevated)',
                                    borderColor: selectedAction === 'select-existing' ? '#6366F1' : 'var(--border-color)'
                                }, children: _jsxs("div", { className: "flex flex-col items-center text-center", children: [_jsx("div", { className: "w-20 h-20 rounded-3xl flex items-center justify-center mb-4", style: { backgroundColor: '#10B98120' }, children: _jsx(Edit3, { className: "w-10 h-10", style: { color: '#10B981' } }) }), _jsx("h3", { className: "text-2xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Manage Existing Agents" }), _jsx("p", { className: "text-sm mb-3", style: { color: 'var(--text-secondary)' }, children: "View, edit, and manage your existing AI agents" }), agents.length > 0 && (_jsxs("span", { className: "px-3 py-1 rounded-full text-xs font-medium", style: { backgroundColor: '#10B98120', color: '#10B981' }, children: [agents.length, " agent", agents.length !== 1 ? 's' : '', " available"] }))] }) })] }), _jsx("div", { className: "flex justify-center", children: _jsxs("button", { onClick: () => setOnboardingStep('welcome'), className: "px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-white/05 transition-colors", style: { color: 'var(--text-secondary)' }, children: [_jsx(ArrowLeft, { className: "w-5 h-5" }), "Back"] }) })] }) }));
    }
    // Model Selection Screen
    if (onboardingStep === 'model-selection') {
        return (_jsx("div", { className: "flex flex-col h-full overflow-auto", style: { backgroundColor: 'var(--bg-base)' }, children: _jsxs("div", { className: "max-w-6xl w-full mx-auto px-8 py-12", children: [_jsx("div", { className: "flex justify-end mb-8", children: _jsx("button", { onClick: handleSkipOnboarding, className: "text-sm px-4 py-2 rounded-lg hover:bg-white/05 transition-colors", style: { color: 'var(--text-secondary)' }, children: "Skip for now" }) }), _jsxs("div", { className: "text-center mb-12", children: [_jsx("h2", { className: "text-4xl font-light mb-3", style: { color: 'var(--text-primary)' }, children: "Choose Your AI Model" }), _jsx("p", { className: "text-lg", style: { color: 'var(--text-secondary)' }, children: "Select the best model for your use case. Each model is optimized for specific tasks." })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8", children: [aiModels.map((model) => {
                                const Icon = model.icon;
                                return (_jsxs("button", { onClick: () => handleModelSelection(model.id), className: `p-6 rounded-2xl border-2 transition-all text-left ${selectedModel === model.id ? 'scale-105' : 'hover:scale-102'}`, style: {
                                        backgroundColor: selectedModel === model.id ? `${model.color}20` : 'var(--bg-elevated)',
                                        borderColor: selectedModel === model.id ? model.color : 'var(--border-color)'
                                    }, children: [_jsxs("div", { className: "flex items-center gap-3 mb-3", children: [_jsx("div", { className: "w-12 h-12 rounded-xl flex items-center justify-center", style: { backgroundColor: `${model.color}20` }, children: _jsx(Icon, { className: "w-6 h-6", style: { color: model.color } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "font-semibold", style: { color: 'var(--text-primary)' }, children: model.name }), _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: model.provider })] }), selectedModel === model.id && (_jsx(CheckCircle2, { className: "w-5 h-5", style: { color: model.color } }))] }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: model.description }), _jsxs("div", { className: "mb-4", children: [_jsx("p", { className: "text-xs font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Best for:" }), _jsx("div", { className: "flex flex-wrap gap-1", children: model.bestFor.slice(0, 3).map((useCase) => (_jsx("span", { className: "px-2 py-1 rounded text-xs", style: {
                                                            backgroundColor: `${model.color}15`,
                                                            color: model.color
                                                        }, children: useCase }, useCase))) })] }), _jsxs("div", { className: "flex items-center justify-between text-xs pt-3 border-t", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("div", { children: [_jsx("span", { style: { color: 'var(--text-secondary)' }, children: "Performance: " }), _jsx("span", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: model.performance })] }), _jsxs("div", { children: [_jsx("span", { style: { color: 'var(--text-secondary)' }, children: "Cost: " }), _jsx("span", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: model.cost })] })] })] }, model.id));
                            }), _jsxs("button", { onClick: () => setShowCustomModelModal(true), className: "p-6 rounded-2xl border-2 border-dashed transition-all text-left hover:scale-102 hover:border-emerald-500", style: {
                                    backgroundColor: 'var(--bg-elevated)',
                                    borderColor: 'var(--border-color)'
                                }, children: [_jsxs("div", { className: "flex items-center gap-3 mb-3", children: [_jsx("div", { className: "w-12 h-12 rounded-xl flex items-center justify-center", style: { backgroundColor: '#10B98120' }, children: _jsx(Settings, { className: "w-6 h-6", style: { color: '#10B981' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "font-semibold", style: { color: 'var(--text-primary)' }, children: "Create Custom Model" }), _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Build Your Own" })] }), _jsx(Plus, { className: "w-5 h-5", style: { color: '#10B981' } })] }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-secondary)' }, children: "Fine-tune an existing model or create your own SLM" }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center gap-2 text-xs", style: { color: 'var(--text-secondary)' }, children: [_jsx(Sparkles, { className: "w-3 h-3", style: { color: '#10B981' } }), _jsx("span", { children: "Fine-tune existing models" })] }), _jsxs("div", { className: "flex items-center gap-2 text-xs", style: { color: 'var(--text-secondary)' }, children: [_jsx(Cpu, { className: "w-3 h-3", style: { color: '#10B981' } }), _jsx("span", { children: "Build Small Language Models (SLM)" })] })] })] })] }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("button", { onClick: () => setOnboardingStep('action-choice'), className: "px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-white/05 transition-colors", style: { color: 'var(--text-secondary)' }, children: [_jsx(ArrowLeft, { className: "w-5 h-5" }), "Back"] }), _jsxs(Button, { onClick: handleContinueWithModel, disabled: !selectedModel, style: {
                                    backgroundColor: selectedModel ? '#6366F1' : 'var(--bg-elevated)',
                                    color: selectedModel ? 'white' : 'var(--text-muted)',
                                    cursor: selectedModel ? 'pointer' : 'not-allowed'
                                }, className: "px-8 py-3 rounded-xl flex items-center gap-2 text-base", children: ["Continue", _jsx(ArrowLeft, { className: "w-5 h-5 rotate-180" })] })] }), showCustomModelModal && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col", children: [_jsxs("div", { className: "px-6 py-4 border-b border-gray-200", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("h2", { className: "text-2xl font-bold text-gray-900", children: "Create Custom AI Model" }), _jsx("button", { onClick: () => {
                                                        setShowCustomModelModal(false);
                                                        setCustomModelType(null);
                                                        setCustomModelStep(1);
                                                        setCustomModelFormData({});
                                                    }, className: "text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "w-6 h-6" }) })] }), _jsx("div", { className: "flex items-center justify-between", children: [
                                                { step: 1, label: 'Welcome' },
                                                { step: 2, label: 'Choose Type' },
                                                { step: 3, label: 'Learn' },
                                                { step: 4, label: 'Dataset' },
                                                { step: 5, label: 'Configure' },
                                                { step: 6, label: 'Review' }
                                            ].map((s, idx) => (_jsxs(React.Fragment, { children: [_jsxs("div", { className: "flex flex-col items-center", children: [_jsx("div", { className: `w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${customModelStep > s.step
                                                                    ? 'bg-emerald-600 text-white'
                                                                    : customModelStep === s.step
                                                                        ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-600'
                                                                        : 'bg-gray-100 text-gray-500'}`, children: customModelStep > s.step ? _jsx(CheckCircle2, { className: "w-4 h-4" }) : s.step }), _jsx("span", { className: `text-xs mt-1 ${customModelStep >= s.step ? 'text-gray-900' : 'text-gray-500'}`, children: s.label })] }), idx < 5 && (_jsx("div", { className: `flex-1 h-0.5 mx-2 ${customModelStep > s.step ? 'bg-emerald-600' : 'bg-gray-200'}` }))] }, s.step))) })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsx(CustomModelWizard, { step: customModelStep, modelType: customModelType, formData: customModelFormData, onTypeSelect: (type) => {
                                            setCustomModelType(type);
                                            setCustomModelStep(3);
                                        }, onFormDataChange: setCustomModelFormData, onNext: () => {
                                            if (customModelStep < 6) {
                                                setCustomModelStep(customModelStep + 1);
                                            }
                                            else {
                                                // Final step - close modal
                                                setShowCustomModelModal(false);
                                                setCustomModelType(null);
                                                setCustomModelStep(1);
                                                setCustomModelFormData({});
                                            }
                                        }, onBack: () => {
                                            if (customModelStep > 1) {
                                                if (customModelStep === 3 && customModelType) {
                                                    // Going back from step 3 to step 2 (type selection)
                                                    setCustomModelStep(2);
                                                }
                                                else {
                                                    setCustomModelStep(customModelStep - 1);
                                                }
                                            }
                                        } }) })] }) }))] }) }));
    }
    // Step 3: Loading
    if (onboardingStep === 'loading') {
        return (_jsx("div", { className: "fixed inset-0 flex items-center justify-center p-4", style: { backgroundColor: 'var(--bg-primary)' }, children: _jsxs("div", { className: "w-full max-w-md space-y-6", children: [_jsx("div", { className: "flex justify-center", children: _jsxs("div", { className: "relative", children: [_jsx("div", { className: "w-32 h-32 border-8 border-gray-200 rounded-full" }), _jsx("div", { className: "absolute top-0 left-0 w-32 h-32 border-8 border-emerald-500 rounded-full border-t-transparent animate-spin" })] }) }), _jsxs("div", { className: "text-center", children: [_jsx("h3", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: loadingMessage }), _jsx("div", { className: "w-full bg-gray-200 rounded-full h-2 overflow-hidden", children: _jsx("div", { className: "bg-gradient-to-r from-emerald-500 to-blue-500 h-2 transition-all duration-500 ease-out", style: { width: `${loadingProgress}%` } }) }), _jsxs("p", { className: "text-sm mt-2", style: { color: 'var(--text-secondary)' }, children: [loadingProgress, "% complete"] })] }), _jsx("div", { className: "p-4 rounded-lg", style: { backgroundColor: 'var(--bg-elevated)' }, children: _jsx("p", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "\uD83D\uDCA1 Did you know? Our AI agents can handle multiple tasks simultaneously while maintaining context across conversations." }) }), _jsx("div", { className: "space-y-2", children: [
                            { label: 'Initializing workspace', threshold: 25 },
                            { label: 'Loading AI models', threshold: 50 },
                            { label: 'Setting up preferences', threshold: 75 },
                            { label: 'Finalizing setup', threshold: 100 }
                        ].map((step, idx) => (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(CheckCircle2, { className: "w-5 h-5 flex-shrink-0", style: { color: loadingProgress >= step.threshold ? '#10B981' : 'var(--text-muted)' } }), _jsx("span", { className: "text-sm", style: { color: 'var(--text-primary)' }, children: step.label })] }, idx))) })] }) }));
    }
    // Main Page (after onboarding)
    // Show loading state
    if (isLoading && agents.length === 0) {
        return (_jsx("div", { className: "flex flex-col items-center justify-center h-full bg-white", children: _jsxs("div", { className: "text-center max-w-md", children: [_jsx("div", { className: "w-16 h-16 mx-auto mb-6", children: _jsx("div", { className: "w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" }) }), _jsx("h3", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Loading Agents" }), _jsx("p", { className: "text-gray-600", children: "Fetching your AI agent workforce..." })] }) }));
    }
    // Show error state
    if (error) {
        return (_jsx("div", { className: "flex flex-col items-center justify-center h-full bg-white p-6", children: _jsxs("div", { className: "text-center max-w-md", children: [_jsx("div", { className: "w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6", children: _jsx(X, { className: "w-8 h-8 text-red-600" }) }), _jsx("h3", { className: "text-xl font-semibold text-gray-900 mb-2", children: "Error Loading Agents" }), _jsx("p", { className: "text-gray-600 mb-6", children: error }), _jsxs("div", { className: "flex items-center gap-3 justify-center", children: [_jsxs(Button, { onClick: () => {
                                    clearError();
                                    fetchAgents();
                                }, className: "flex items-center gap-2", children: [_jsx(Bot, { className: "h-4 w-4" }), "Try Again"] }), _jsx(Button, { variant: "outline", onClick: clearError, children: "Dismiss" })] })] }) }));
    }
    return (_jsx(AgentList, { onAgentSelect: handleAgentSelect, onCreateAgent: handleCreateAgent, onShowOnboarding: handleShowOnboarding }));
}
export function AgentsPage() {
    return (_jsx("div", { className: "h-full flex flex-col", children: _jsxs(Routes, { children: [_jsx(Route, { index: true, element: _jsx(AgentsMainPage, {}) }), _jsx(Route, { path: "create", element: _jsx(AgentCreationPage, {}) }), _jsx(Route, { path: ":agentId", element: _jsx(AgentDetailsPage, {}) })] }) }));
}
